import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TableHeaderCell, ActiveFilters } from '@/components/ui/table-header-cell';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  Pause,
  RefreshCw,
  FileText,
  Play,
  XCircle,
  CalendarClock,
  CalendarDays,
  List,
  LayoutGrid,
  Flag
} from 'lucide-react';
import { WS_STATUS_CONFIG, WS_STATUSES, WS_STATUS_ORDER } from '@/config/statusWorkflow';
import { EVENT_TYPES } from '@/config/eventTypes';

// Event Type filter options - use authoritative list from config
const EVENT_TYPE_OPTIONS = EVENT_TYPES.map(type => ({
  value: type,
  label: type
}));

// Status order for sorting (from workflow config) - include CANCELLED and RESCHEDULE at end
const STATUS_ORDER = [...WS_STATUS_ORDER, 'CANCELLED', 'RESCHEDULE'];
const PRIORITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const COMPLEXITY_ORDER = ['LIGHT', 'STANDARD', 'HEAVY'];

/**
 * Check if a work schedule is overdue.
 * A schedule is overdue if:
 * - Status is "SCHEDULED"
 * - Schedule end date (or start date if no end) is before today
 */
const isScheduleOverdue = (schedule) => {
  if (schedule.status !== 'SCHEDULED') return false;
  
  // Use end date if available, otherwise start date
  const dateStr = schedule.schedule_end_date || schedule.schedule_start_date || schedule.schedule_date;
  if (!dateStr) return false;
  
  // Parse date string (YYYY-MM-DD format)
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return false;
  
  // Create date at end of day for the schedule date
  const scheduleDate = new Date(year, month - 1, day, 23, 59, 59);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return scheduleDate < today;
};

/**
 * Format schedule date range consistently without timezone shifts.
 * Input: start="2026-02-08", end="2026-02-10"
 * Output: "2/8/2026 – 2/10/2026" (if different) or "2/8/2026" (if same)
 */
const formatScheduleDateRange = (startStr, endStr) => {
  if (!startStr) return '-';
  
  // Parse start date
  const [startYear, startMonth, startDay] = startStr.split('-').map(Number);
  if (!startYear || !startMonth || !startDay) return startStr;
  const startFormatted = `${startMonth}/${startDay}/${startYear}`;
  
  // If no end date or same as start, return single date
  if (!endStr || endStr === startStr) {
    return startFormatted;
  }
  
  // Parse end date
  const [endYear, endMonth, endDay] = endStr.split('-').map(Number);
  if (!endYear || !endMonth || !endDay) return startFormatted;
  const endFormatted = `${endMonth}/${endDay}/${endYear}`;
  
  return `${startFormatted} – ${endFormatted}`;
};

/**
 * Legacy format for single date (backwards compatibility)
 */
const formatScheduleDate = (dateStr) => {
  if (!dateStr) return '-';
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  return `${month}/${day}/${year}`;
};

// Filter options - generated from WS_STATUS_CONFIG
// FEATURE 1: Exclude CLOSED and CANCELLED since they don't appear in the list
const STATUS_OPTIONS = WS_STATUSES
  .filter(s => s !== 'CLOSED' && s !== 'CANCELLED')
  .map(s => ({
    value: s,
    label: WS_STATUS_CONFIG[s]?.label || s
  }));

const PRIORITY_OPTIONS = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' }
];

const COMPLEXITY_OPTIONS = [
  { value: 'LIGHT', label: 'Light' },
  { value: 'STANDARD', label: 'Standard' },
  { value: 'HEAVY', label: 'Heavy' }
];

const WorkSchedulesPage = () => {
  const navigate = useNavigate();
  const { api, currentFacility, hasRole } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  // FEATURE 1: Removed includeArchived toggle - Closed/Cancelled jobs accessed via Service Requests
  
  // Sort state
  const [sort, setSort] = useState({ field: 'schedule_date', direction: 'desc' });
  
  // Filter state (multi-select for each field)
  const [filters, setFilters] = useState({
    status: [],
    priority: [],
    complexity: [],
    facility_id: [],
    event_type: []
  });

  // Check if "All facilities" is selected and user is Manager+
  const showFacilityColumn = currentFacility === 'all' && hasRole(['ADMIN', 'MANAGER']);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (currentFacility && currentFacility !== 'all') {
        params.facility_id = currentFacility;
      }
      // FEATURE 1: Backend automatically excludes Closed/Cancelled
      const response = await api.get('/work-schedules', { params });
      setSchedules(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch work schedules:', error);
      toast.error('Failed to load work schedules');
    } finally {
      setLoading(false);
    }
  }, [api, currentFacility]);

  const fetchFacilities = useCallback(async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchSchedules();
    fetchFacilities();
  }, [fetchSchedules, fetchFacilities]);

  // Build facility filter options from data
  const facilityOptions = useMemo(() => {
    return facilities.map(f => ({ value: f.id, label: f.name }));
  }, [facilities]);

  // Apply filters and sorting to schedules
  const filteredAndSortedSchedules = useMemo(() => {
    if (!Array.isArray(schedules) || schedules.length === 0) return [];
    let result = [...schedules];

    // Apply search
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(s =>
        s.job_number?.toLowerCase().includes(searchLower) ||
        s.car_mark?.toLowerCase().includes(searchLower) ||
        s.car_number?.toLowerCase().includes(searchLower) ||
        s.job_lead_name?.toLowerCase().includes(searchLower) ||
        s.facility_name?.toLowerCase().includes(searchLower) ||
        s.repair_site?.toLowerCase().includes(searchLower) ||
        s.event_type?.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    if (filters.status.length > 0) {
      result = result.filter(s => filters.status.includes(s.status));
    }
    if (filters.priority.length > 0) {
      result = result.filter(s => filters.priority.includes(s.priority));
    }
    if (filters.complexity.length > 0) {
      result = result.filter(s => filters.complexity.includes(s.complexity));
    }
    if (filters.facility_id.length > 0) {
      result = result.filter(s => filters.facility_id.includes(s.facility_id));
    }
    // Event Type filter
    if (filters.event_type.length > 0) {
      result = result.filter(s => filters.event_type.includes(s.event_type));
    }

    // Apply sorting
    if (sort) {
      result.sort((a, b) => {
        let aVal = a[sort.field];
        let bVal = b[sort.field];

        // Handle special sort orders
        if (sort.field === 'status') {
          aVal = STATUS_ORDER.indexOf(aVal);
          bVal = STATUS_ORDER.indexOf(bVal);
        } else if (sort.field === 'priority') {
          aVal = PRIORITY_ORDER.indexOf(aVal);
          bVal = PRIORITY_ORDER.indexOf(bVal);
        } else if (sort.field === 'complexity') {
          aVal = COMPLEXITY_ORDER.indexOf(aVal);
          bVal = COMPLEXITY_ORDER.indexOf(bVal);
        } else if (sort.field === 'schedule_date' || sort.field === 'created_at') {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        } else if (sort.field === 'days_open') {
          aVal = parseInt(aVal) || 0;
          bVal = parseInt(bVal) || 0;
        } else if (sort.field === 'car_number') {
          aVal = parseInt(aVal) || 0;
          bVal = parseInt(bVal) || 0;
        }

        // String comparison for other fields
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [schedules, search, filters, sort]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleFilterChange = (field, values) => {
    setFilters(prev => ({ ...prev, [field]: values }));
  };

  const handleClearFilter = (field) => {
    setFilters(prev => ({ ...prev, [field]: [] }));
  };

  const handleClearAllFilters = () => {
    setFilters({ status: [], priority: [], complexity: [], facility_id: [], event_type: [] });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/work-schedules/${deleteId}`);
      toast.success('Work schedule deleted');
      setDeleteId(null);
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to delete work schedule');
    }
  };

  const getStatusConfig = (status) => {
    return WS_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  };

  const getPriorityClass = (priority) => {
    const priorityMap = {
      'CRITICAL': 'bg-red-100 text-red-700',
      'HIGH': 'bg-orange-100 text-orange-700',
      'MEDIUM': 'bg-yellow-100 text-yellow-700',
      'LOW': 'bg-green-100 text-green-700'
    };
    return priorityMap[priority] || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SCHEDULED': return <Calendar className="w-3 h-3 mr-1" />;
      case 'IN_PROCESS': return <Play className="w-3 h-3 mr-1" />;
      case 'ON_HOLD': return <Pause className="w-3 h-3 mr-1" />;
      case 'QA_REVIEW': return <FileText className="w-3 h-3 mr-1" />;
      case 'RELEASE': return <RefreshCw className="w-3 h-3 mr-1" />;
      case 'BILLING': return <FileText className="w-3 h-3 mr-1" />;
      case 'INVOICED': return <CheckCircle2 className="w-3 h-3 mr-1" />;
      case 'CLOSED': return <CheckCircle2 className="w-3 h-3 mr-1" />;
      case 'CANCELLED': return <XCircle className="w-3 h-3 mr-1" />;
      case 'RESCHEDULE': return <CalendarClock className="w-3 h-3 mr-1" />;
      default: return null;
    }
  };

  const canManage = hasRole(['ADMIN', 'MANAGER', 'OPERATOR']);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="work-schedules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">Work Schedules</h1>
          <p className="text-[#5A5A5A] text-sm mt-1">
            {filteredAndSortedSchedules.length} schedule{filteredAndSortedSchedules.length !== 1 ? 's' : ''}
            {schedules.length !== filteredAndSortedSchedules.length && ` (filtered from ${schedules.length})`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Table / Visual view toggle — Visual Schedule is a sibling view of
              the same data, hidden from Technicians entirely. The route guard
              also enforces this; this just keeps the UI clean. */}
          {canManage && (
            <div className="inline-flex items-center gap-2" data-testid="ws-view-toggle">
              <Button
                variant="default"
                size="sm"
                className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                data-testid="ws-view-table-btn"
                aria-pressed="true"
              >
                <List className="w-4 h-4 mr-1" />
                Table
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/work-schedules/visual')}
                data-testid="ws-view-visual-btn"
                aria-pressed="false"
              >
                <LayoutGrid className="w-4 h-4 mr-1" />
                Visual
              </Button>
            </div>
          )}
          {canManage && (
            (() => {
              // Phase 5: Scheduling is strictly single-facility. Block the
              // entry point when Admin/Manager is viewing "All Facilities"
              // (currentFacility === 'all'). Operators/Techs never see the
              // 'all' option so this is a no-op for them.
              const blocked = currentFacility === 'all';
              const btn = (
                <Button
                  onClick={() => !blocked && navigate('/work-schedules/add')}
                  disabled={blocked}
                  className="bg-[#005EF8] hover:bg-[#0047C2] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="add-work-schedule-btn"
                  title={blocked ? 'Select a specific facility to create a work schedule.' : undefined}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Work Schedule
                </Button>
              );
              return blocked
                ? (
                  <div className="flex flex-col items-end gap-1" data-testid="add-ws-blocked">
                    {btn}
                    <p className="text-xs text-[#5A5A5A]">
                      Select a specific facility to create a work schedule.
                    </p>
                  </div>
                )
                : btn;
            })()
          )}
        </div>
      </div>

      {/* Search and Active Filters */}
      <div className="space-y-3">
        <form onSubmit={handleSearch} className="flex gap-2 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <Input
              placeholder="Search by SR #, car, job lead, repair site..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          {/* FEATURE 1: Removed "Show Archived" toggle - Closed/Cancelled jobs accessed via Service Requests */}
        </form>

        <ActiveFilters
          filters={filters}
          onClear={handleClearFilter}
          onClearAll={handleClearAllFilters}
        />
      </div>

      {/* Table - Column order: SR#, Car, Event type, Complexity, Repair Site, Schedule date, Job lead, Priority, Days open, Status, Actions */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8] mx-auto"></div>
              <p className="text-[#5A5A5A] mt-4">Loading...</p>
            </div>
          ) : filteredAndSortedSchedules.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#323033] mb-2">No work schedules found</h3>
              <p className="text-[#5A5A5A] mb-6">
                {schedules.length > 0 ? 'Try adjusting your filters' : 'Create a work schedule from an existing service request'}
              </p>
              {canManage && schedules.length === 0 && currentFacility !== 'all' && (
                <Button
                  onClick={() => navigate('/work-schedules/add')}
                  className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Work Schedule
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    {/* Facility column FIRST - only for Manager+ when "All facilities" selected */}
                    {showFacilityColumn && (
                      <TableHeaderCell
                        label="Facility"
                        field="facility_name"
                        sortable
                        filterable
                        filterOptions={facilityOptions}
                        currentSort={sort}
                        currentFilters={filters.facility_id}
                        onSort={setSort}
                        onFilter={(_, values) => handleFilterChange('facility_id', values)}
                      />
                    )}
                    {/* 1. SR # */}
                    <TableHeaderCell
                      label="SR #"
                      field="job_number"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 2. Car */}
                    <TableHeaderCell
                      label="Car"
                      field="car_mark"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 3. Event Type */}
                    <TableHeaderCell
                      label="Event Type"
                      field="event_type"
                      sortable
                      filterable
                      filterOptions={EVENT_TYPE_OPTIONS}
                      currentSort={sort}
                      currentFilters={filters.event_type}
                      onSort={setSort}
                      onFilter={handleFilterChange}
                    />
                    {/* 4. Complexity */}
                    <TableHeaderCell
                      label="Complexity"
                      field="complexity"
                      sortable
                      filterable
                      filterOptions={COMPLEXITY_OPTIONS}
                      currentSort={sort}
                      currentFilters={filters.complexity}
                      onSort={setSort}
                      onFilter={handleFilterChange}
                    />
                    {/* 5. Repair Site (unified naming) */}
                    <TableHeaderCell
                      label="Repair Site"
                      field="repair_site"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 6. Schedule Date */}
                    <TableHeaderCell
                      label="Schedule Date"
                      field="schedule_date"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 7. Job Lead (name only, no facility concatenation) */}
                    <TableHeaderCell
                      label="Job Lead"
                      field="job_lead_name"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 8. Priority */}
                    <TableHeaderCell
                      label="Priority"
                      field="priority"
                      sortable
                      filterable
                      filterOptions={PRIORITY_OPTIONS}
                      currentSort={sort}
                      currentFilters={filters.priority}
                      onSort={setSort}
                      onFilter={handleFilterChange}
                    />
                    {/* 9. Days Open (based on creation date) */}
                    <TableHeaderCell
                      label="Days Open"
                      field="days_open"
                      sortable
                      currentSort={sort}
                      onSort={setSort}
                    />
                    {/* 10. Status */}
                    <TableHeaderCell
                      label="Status"
                      field="status"
                      sortable
                      filterable
                      filterOptions={STATUS_OPTIONS}
                      currentSort={sort}
                      currentFilters={filters.status}
                      onSort={setSort}
                      onFilter={handleFilterChange}
                    />
                    {/* 11. Actions */}
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedSchedules.map((schedule) => {
                    const isDed = schedule.schedule_type === 'DEDICATED_SITE';
                    const detailPath = isDed
                      ? `/work-schedules/dedicated/${schedule.id}`
                      : `/work-schedules/${schedule.id}`;
                    return (
                    <tr
                      key={schedule.id}
                      data-testid={`ws-row-${schedule.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(detailPath)}
                    >
                      {/* Facility FIRST */}
                      {showFacilityColumn && (
                        <td>
                          <span className="text-sm">{schedule.facility_name || '-'}</span>
                        </td>
                      )}
                      {/* 1. SR # (or "Dedicated Site" label). */}
                      <td>
                        {schedule.schedule_type === 'DEDICATED_SITE' ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold border border-indigo-200"
                            data-testid={`dedicated-site-badge-${schedule.id}`}
                            title="Non-SR work at a Dedicated Site"
                          >
                            Dedicated Site
                          </span>
                        ) : (
                          <span className="font-medium text-[#005EF8]">
                            {schedule.job_number}
                          </span>
                        )}
                      </td>
                      {/* 2. Car */}
                      <td>
                        {schedule.schedule_type === 'DEDICATED_SITE' ? (
                          <span className="text-[#9A9A9A] text-sm italic">N/A</span>
                        ) : (
                          <>
                            <span className="font-medium">{schedule.car_mark}</span>
                            <span className="text-[#5A5A5A] ml-1">{schedule.car_number}</span>
                          </>
                        )}
                      </td>
                      {/* 3. Event Type */}
                      <td>
                        {schedule.schedule_type === 'DEDICATED_SITE' ? (
                          <span className="text-[#9A9A9A] text-sm italic">N/A</span>
                        ) : (
                          <span className="text-sm">{schedule.event_type || schedule.scope_of_work || '-'}</span>
                        )}
                      </td>
                      {/* 4. Complexity with Specialist Review marker */}
                      <td>
                        {schedule.schedule_type === 'DEDICATED_SITE' ? (
                          <span className="text-[#9A9A9A] text-sm italic">N/A</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-[#5A5A5A]">{schedule.complexity || '-'}</span>
                            {schedule.needs_specialist_review && (
                              <span
                                className="w-2.5 h-2.5 rounded-full bg-purple-500 flex-shrink-0"
                                title="Needs specialist review"
                                data-testid="specialist-review-marker"
                              />
                            )}
                          </div>
                        )}
                      </td>
                      {/* 5. Repair Site (or Dedicated Site name). */}
                      <td>
                        <span className="text-sm">
                          {schedule.dedicated_site_name
                            || schedule.repair_site
                            || schedule.site_name
                            || '-'}
                        </span>
                      </td>
                      {/* 6. Schedule Date (Range) - Red when overdue */}
                      <td>
                        <div className={`flex items-center gap-1 text-sm ${isScheduleOverdue(schedule) ? 'text-red-600 font-medium' : 'text-[#5A5A5A]'}`}>
                          <Calendar className={`w-3.5 h-3.5 ${isScheduleOverdue(schedule) ? 'text-red-600' : ''}`} />
                          {formatScheduleDateRange(schedule.schedule_start_date || schedule.schedule_date, schedule.schedule_end_date)}
                        </div>
                      </td>
                      {/* 7. Job Lead */}
                      <td>
                        <span className="text-sm">{schedule.job_lead_name || '-'}</span>
                      </td>
                      {/* 8. Priority */}
                      <td>
                        {isDed ? (
                          <span className="text-[#9A9A9A] text-sm italic">N/A</span>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityClass(schedule.priority)}`}>
                            {schedule.priority === 'CRITICAL' && <AlertCircle className="w-3 h-3 inline mr-1" />}
                            {schedule.priority}
                          </span>
                        )}
                      </td>
                      {/* 9. Days Open */}
                      <td>
                        {isDed ? (
                          <span className="text-[#9A9A9A] text-sm italic">N/A</span>
                        ) : (
                          <span
                            className="font-medium text-sm"
                            style={{ color: schedule.days_open_color }}
                          >
                            <Clock className="w-3.5 h-3.5 inline mr-1" />
                            {schedule.days_open}d
                          </span>
                        )}
                      </td>
                      {/* 10. Status with Overdue badge */}
                      <td>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusConfig(schedule.status).color}`}>
                              {getStatusIcon(schedule.status)}
                              {getStatusConfig(schedule.status).label}
                            </span>
                            {/* 2026-08-22 — compact Billing Issue flag icon (WS Billing only) */}
                            {schedule.status === 'BILLING' && schedule.has_active_billing_issue && (
                              <span
                                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100"
                                title="Active billing issue"
                                aria-label="Active billing issue"
                                data-testid={`ws-billing-issue-flag-${schedule.id}`}
                              >
                                <Flag className="w-3.5 h-3.5 text-amber-600 fill-amber-500" aria-hidden="true" />
                              </span>
                            )}
                            {isScheduleOverdue(schedule) && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700" title="Past scheduled date">
                                Overdue
                              </span>
                            )}
                          </div>
                          {/* Archived indicator */}
                          {schedule.archived && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                              Archived
                            </span>
                          )}
                        </div>
                      </td>
                      {/* 11. Actions */}
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`ws-actions-${schedule.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(detailPath)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {/* Wave 2 #5: View Car File is hidden for Dedicated Site rows. */}
                            {!isDed && (
                              <DropdownMenuItem onClick={() => navigate(`/car-files/${schedule.car_mark}/${schedule.car_number}`)}>
                                <FileText className="w-4 h-4 mr-2" />
                                View Car File
                              </DropdownMenuItem>
                            )}
                            {canManage && (
                              <>
                                <DropdownMenuItem onClick={() => navigate(`/work-schedules/${schedule.id}/edit`)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setDeleteId(schedule.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Work Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the work schedule and all associated line items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkSchedulesPage;
